import { apiFetch } from './client';

export type OrderStatus =
  | 'pending'
  | 'accepted'
  | 'picked_up'
  | 'in_transit'
  | 'delivered'
  | 'cancelled'
  | 'failed';

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export interface OrderPartyUser {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  role?: string;
  profilePictureUrl?: string | null;
}

export interface Order {
  id: string;
  trackingCode: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  price: number;
  currency: string;
  distance: number;
  pickupAddress: string;
  deliveryAddress: string;
  recipientName: string;
  recipientPhone?: string;
  senderName: string;
  senderPhone?: string;
  scheduledPickupTime?: string;
  actualPickupTime?: string;
  actualDeliveryTime?: string;
  createdAt: string;
  updatedAt: string;
  rating?: number;
  feedback?: string;
  Customer?: OrderPartyUser | null;
  Deliverer?: {
    id: string;
    companyName?: string;
    vehicleType?: string;
    rating?: number;
    User?: OrderPartyUser | null;
  } | null;
}

export interface OrdersListResponse {
  success: true;
  data: Order[];
  pagination: { total: number; limit: number; offset: number };
}

export interface FetchOrdersParams {
  limit?: number;
  offset?: number;
  status?: OrderStatus;
  search?: string;
}

export const fetchOrders = (params: FetchOrdersParams = {}) =>
  apiFetch<OrdersListResponse>('/api/v1/orders', { query: params as Record<string, string | number | undefined | null> });

export const fetchOrder = (id: string) =>
  apiFetch<{ success: true; data: Order }>(`/api/v1/orders/${id}`);

/** Suivi public d'une commande par son code de tracking (pas d'auth). */
export interface PublicTrackingInfo {
  trackingCode: string;
  status: OrderStatus;
  pickupAddress: string;
  deliveryAddress: string;
  scheduledPickupTime?: string;
  actualPickupTime?: string;
  estimatedDeliveryTime?: string;
  actualDeliveryTime?: string;
  recipientName: string;
  senderName: string;
  distance: number;
}

export const trackPublicOrder = (code: string) =>
  apiFetch<{ success: true; data: PublicTrackingInfo }>(`/api/v1/orders/tracking/${encodeURIComponent(code)}`, {
    auth: false
  });

export interface CreateOrderInput {
  delivererId: string;
  customerId?: string;
  platformPartnerId?: string;
  externalOrderId?: string;
  externalVendorId?: string;
  wpVendorId?: number;
  paymentMethod?: 'cash' | 'credit_card' | 'mobile_money' | 'bank_transfer' | 'platform';
  weight?: number;
  volume?: number;
  pickupAddress: string;
  pickupLatitude: number;
  pickupLongitude: number;
  deliveryAddress: string;
  deliveryLatitude: number;
  deliveryLongitude: number;
  scheduledPickupTime?: string;
  recipientName: string;
  recipientPhone: string;
  recipientEmail?: string;
  senderName: string;
  senderPhone: string;
  senderEmail?: string;
  notes?: string;
  urgency?: boolean;
}

export const createOrder = (input: CreateOrderInput) =>
  apiFetch<{ success: true; data: Order }>('/api/v1/orders', { method: 'POST', body: input });

export const updateOrderStatus = (id: string, status: OrderStatus) =>
  apiFetch<{ success: true; data: Order }>(`/api/v1/orders/${id}/status`, {
    method: 'PATCH',
    body: { status }
  });

export const updatePaymentStatus = (id: string, paymentStatus: PaymentStatus) =>
  apiFetch<{ success: true; data: Order }>(`/api/v1/orders/${id}/payment-status`, {
    method: 'PATCH',
    body: { paymentStatus }
  });

export const cancelOrder = (id: string, reason?: string) =>
  apiFetch<{ success: true; data: Order }>(`/api/v1/orders/${id}/cancel`, {
    method: 'POST',
    body: { reason }
  });

export const rateOrder = (id: string, rating: number, feedback?: string) =>
  apiFetch<{ success: true; data: Order }>(`/api/v1/orders/${id}/rate`, {
    method: 'POST',
    body: { rating, feedback }
  });
